import React from 'react';

type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
type HeadingSize = 'sm' | '2xl';
type HeadingWeight = 'medium' | 'bold';
type HeadingColor = 'black' | 'dark';
type HeadingFont = 'heading' | 'outfit';

interface HeadingProps {
    children: React.ReactNode;
    level?: HeadingLevel;
    size?: HeadingSize;
    weight?: HeadingWeight;
    color?: HeadingColor;
    font?: HeadingFont;
    className?: string;
    responsive?: boolean;
    centered?: boolean;
    capitalized?: boolean;
    maxWidth?: string;
}

const responsiveSizes = {
    '2xl': 'text-3xl sm:text-4xl md:text-5xl lg:text-6xl'
};

const simpleSizes = {
    'sm': 'text-2xl'
};

const weights = {
    medium: 'font-medium',
    bold: 'font-bold'
};

const colors = {
    black: 'text-black',
    dark: 'text-[#0E0C0C]'
};

const fonts = {
    heading: 'font-heading',
    outfit: 'font-[Outfit]'
};

const maxWidths = {
    'none': ''
};


export const Heading = ({
    children,
    level = 'h1',
    size = '2xl',
    weight = 'bold',
    color = 'black',
    font = 'heading',
    className = '',
    responsive = true,
    centered = false,
    capitalized = false,
    maxWidth = 'none'
}: HeadingProps) => {
    const Component = level as HeadingLevel;

    const sizeClasses = responsive ? responsiveSizes[size] : simpleSizes[size];
    const weightClass = weights[weight];
    const colorClass = colors[color];
    const fontClass = fonts[font];
    const maxWidthClass = maxWidths[maxWidth as keyof typeof maxWidths] || maxWidth;

    const classes = [
        fontClass,
        sizeClasses,
        weightClass,
        colorClass,
        centered && 'text-center',
        capitalized && 'capitalize',
        centered && maxWidthClass && 'mx-auto',
        maxWidthClass,
        className
    ].filter(Boolean).join(' ');

    return (
        <Component className={classes}>
            {children}
        </Component>
    );
};


export const SectionTitle = (props: Omit<HeadingProps, 'level' | 'size' | 'weight' | 'font' | 'color'>) => (
    <Heading
        level="h2"
        size="sm"
        weight="medium"
        color="dark"
        font="outfit"
        responsive={false}
        className="text-[1.5rem] leading-[1.875rem]"
        {...props}
    />
); 